import React from "react";
import { CheckIcon, XIcon, PencilIcon } from "@heroicons/react/outline";

const RateComparisonCard = ({
  currentRates,
  apiRates,
  onConfirm,
  onCancel,
  onEdit,
}) => {
  const metals = ["GOLD", "SILVER", "PLATINUM"];

  const calculateDifference = (current, api) => {
    if (!current || !api) return 0;
    return (((api - current) / current) * 100).toFixed(2);
  };

  const getDifferenceColor = (diff) => {
    const numDiff = parseFloat(diff);
    if (Math.abs(numDiff) < 0.5) return "text-gray-600";
    if (numDiff > 0) return "text-green-600";
    return "text-red-600";
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 py-3 sm:py-4">
        <h3 className="text-lg sm:text-xl font-bold text-white">
          API Rate Comparison
        </h3>
        <p className="text-blue-100 text-xs sm:text-sm mt-1">
          Review and confirm market rates
        </p>
      </div>

      <div className="p-4 sm:p-6">
        <div className="space-y-3 sm:space-y-4">
          {metals.map((metal) => {
            const current = currentRates?.[metal]?.baseRate;
            const api = apiRates?.[metal]?.baseRate;
            const diff = calculateDifference(current, api);

            return (
              <div key={metal} className="border rounded-lg p-3 sm:p-4">
                <h4 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3">
                  {metal}
                </h4>

                {/* Mobile: Stack vertically, Tablet/Desktop: Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="flex justify-between sm:block">
                    <p className="text-xs sm:text-sm text-gray-500">
                      Current Rate
                    </p>
                    <p className="text-lg sm:text-xl font-bold">
                      ₹{current?.toLocaleString("en-IN") || "-"}
                    </p>
                  </div>

                  <div className="flex justify-between sm:block">
                    <p className="text-xs sm:text-sm text-gray-500">
                      Market Rate
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">
                      ₹{api?.toLocaleString("en-IN") || "-"}
                    </p>
                  </div>

                  <div className="flex justify-between sm:block">
                    <p className="text-xs sm:text-sm text-gray-500">
                      Difference
                    </p>
                    <p
                      className={`text-lg sm:text-xl font-bold ${getDifferenceColor(
                        diff
                      )}`}
                    >
                      {diff > 0 ? "+" : ""}
                      {diff}%
                    </p>
                  </div>
                </div>

                {Math.abs(parseFloat(diff)) > 2 && (
                  <div className="mt-2 sm:mt-3 p-2 bg-yellow-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-yellow-800">
                      ⚠️ Significant difference detected
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-800">
            <strong>Note:</strong> Confirming will update all rates to the
            market values shown above. This action will affect all branches
            using market-linked rates.
          </p>
        </div>

        {/* Mobile: Stack buttons, Tablet/Desktop: Row */}
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <button
            onClick={onCancel}
            className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 
              hover:bg-gray-50 flex items-center justify-center text-sm sm:text-base"
          >
            <XIcon className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
            Cancel
          </button>
          {onEdit && (
            <button
              onClick={onEdit}
              className="px-3 sm:px-4 py-2 border border-blue-300 bg-blue-50 rounded-lg 
                text-blue-700 hover:bg-blue-100 flex items-center justify-center text-sm sm:text-base"
            >
              <PencilIcon className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
              Edit Values
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 sm:px-6 py-2 bg-green-600 text-white rounded-lg 
              hover:bg-green-700 flex items-center justify-center text-sm sm:text-base"
          >
            <CheckIcon className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
            Confirm & Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default RateComparisonCard;
